import { useState } from 'react'
import { Briefcase, Shield, DollarSign, Users, Package, CheckSquare, Calendar, Cloud, Share2, TrendingUp, FileText, Settings, BarChart3, Zap, Search, ChevronDown } from 'lucide-react'

export default function ToolsShowcasePage() {
  const [expandedCategory, setExpandedCategory] = useState(null)

  const toolCategories = [
    {
      id: 'help-desk',
      title: 'HELP DESK & SUPPORT',
      icon: Briefcase,
      color: 'from-blue-600 to-cyan-600',
      description: 'Enterprise-grade multi-channel support platform',
      tools: [
        { name: 'AI Control Center', desc: 'Centralized hub for all AI-powered operations and automation' },
        { name: 'ITIL Help Desk', desc: 'Complete ITIL-compliant help desk management system' },
        { name: 'Channel Monitor', desc: 'Monitor support across email, Discord, LinkedIn, SMS & voice' },
        { name: 'Unified Queue', desc: 'Single inbox for all support tickets from all channels' },
        { name: 'Chat', desc: 'Real-time chat support with customer communication' },
        { name: 'Knowledge Base', desc: 'Self-service article library for customers' },
        { name: 'Student Questions', desc: 'Dedicated module for training/student inquiries' },
        { name: 'Call Logs', desc: 'Complete call recording and logging system with analytics' },
        { name: 'TextNow Phone Config', desc: 'SMS and phone integration for support channels' },
      ]
    },
    {
      id: 'crm',
      title: 'CRM & SALES',
      icon: TrendingUp,
      color: 'from-purple-600 to-pink-600',
      description: 'Complete customer relationship management suite',
      tools: [
        { name: 'Customer Sales', desc: 'Comprehensive sales management and customer tracking' },
        { name: 'Customers', desc: 'Customer database with profiles and history' },
        { name: 'Leads', desc: 'Lead capture and management with scoring' },
        { name: 'Opportunities', desc: 'Sales pipeline and opportunity tracking' },
        { name: 'Activities', desc: 'Activity logging and task management' },
        { name: 'Contacts', desc: 'Contact directory with relationship mapping' },
        { name: 'Sales Pipeline', desc: 'Visual pipeline management with kanban boards' },
        { name: 'Sales Forecasting', desc: 'AI-powered revenue prediction and forecasting' },
        { name: 'Sales Reports', desc: 'Comprehensive sales analytics and reporting' },
      ]
    },
    {
      id: 'inventory',
      title: 'INVENTORY & SUPPLY CHAIN',
      icon: Package,
      color: 'from-green-600 to-emerald-600',
      description: 'Complete supply chain and inventory management',
      tools: [
        { name: 'Inventory & Supply Chain', desc: 'End-to-end supply chain visibility' },
        { name: 'Products', desc: 'Product catalog with specifications' },
        { name: 'Inventory', desc: 'Real-time inventory tracking and management' },
        { name: 'Purchase Orders', desc: 'PO creation and vendor management' },
        { name: 'Suppliers', desc: 'Supplier database and relationship management' },
        { name: 'Stock Alerts', desc: 'Automatic alerts for low stock levels' },
        { name: 'Shipments', desc: 'Real-time shipment tracking and logistics' },
        { name: 'Supply Reports', desc: 'Analytics and supply chain reporting' },
      ]
    },
    {
      id: 'hr',
      title: 'HR & EMPLOYEE MANAGEMENT',
      icon: Users,
      color: 'from-orange-600 to-red-600',
      description: 'Complete human resources and payroll platform',
      tools: [
        { name: 'HR Dashboard', desc: 'Comprehensive HR analytics and KPIs' },
        { name: 'Time & Attendance', desc: 'Employee time tracking and attendance management' },
        { name: 'Payroll', desc: 'Automated payroll processing and management' },
        { name: 'Leave Management', desc: 'Leave request and approval workflows' },
        { name: 'Performance Reviews', desc: 'Performance evaluation and feedback system' },
        { name: 'Benefits', desc: 'Employee benefits administration' },
        { name: 'Onboarding', desc: 'New hire onboarding and training' },
        { name: 'HR Analytics', desc: 'HR metrics, headcount, and workforce analytics' },
        { name: 'Employee Collaboration', desc: 'Employee directory and collaboration tools' },
      ]
    },
    {
      id: 'financial',
      title: 'FINANCIAL & ACCOUNTING',
      icon: DollarSign,
      color: 'from-yellow-600 to-amber-600',
      description: 'Complete financial management suite',
      tools: [
        { name: 'Financial Monitor', desc: 'Real-time financial metrics and dashboards' },
        { name: 'Tax Prep', desc: 'AI-assisted tax preparation and filing' },
        { name: 'AI Financial Assist', desc: 'AI-powered financial advice and optimization' },
        { name: 'Accounting & General Ledger', desc: 'Complete accounting system with GL' },
        { name: 'QuickBooks/Xero Integration', desc: 'Integration with major accounting platforms' },
      ]
    },
    {
      id: 'security',
      title: 'SECURITY & THREAT DETECTION',
      icon: Shield,
      color: 'from-red-600 to-pink-600',
      description: 'Enterprise-grade security and threat monitoring',
      tools: [
        { name: 'SIEM Tool', desc: 'Security Information & Event Management with AI SOC Analyst' },
        { name: 'EDR Tool', desc: 'Endpoint Detection & Response with threat intelligence' },
        { name: 'App Security', desc: 'Application security scanning and vulnerability management' },
        { name: 'Guardbot Integration', desc: 'Integration with Guardbot AI Security platform' },
        { name: 'Sandbox Analysis', desc: 'Joe Box malware analysis and sandbox environment' },
        { name: 'Device Protection', desc: 'Device hardening and endpoint protection' },
      ]
    },
    {
      id: 'auditor',
      title: 'AUDITING & COMPLIANCE',
      icon: CheckSquare,
      color: 'from-indigo-600 to-blue-600',
      description: 'Comprehensive auditing and compliance framework',
      tools: [
        { name: 'Live Audit', desc: 'Real-time security auditing and monitoring' },
        { name: 'Write Report', desc: 'Automated report generation and documentation' },
        { name: 'Audit History', desc: 'Complete audit trail and historical data' },
        { name: 'Comprehensive Audit Tool', desc: 'Full audit management suite with workflows' },
        { name: 'Penetration Testing', desc: 'Automated penetration testing and vulnerability scanning' },
        { name: 'Vulnerability Assessment', desc: 'Detailed vulnerability analysis and remediation' },
        { name: 'Dark Web Monitor', desc: 'Monitor dark web for exposed credentials and breaches' },
        { name: 'Phishing Sim Tool', desc: 'Employee phishing simulation for security awareness training' },
      ]
    },
    {
      id: 'marketing',
      title: 'MARKETING & SOCIAL MEDIA',
      icon: Share2,
      color: 'from-pink-600 to-rose-600',
      description: 'Marketing automation and social media management',
      tools: [
        { name: 'Social Media Manager', desc: 'Multi-platform social media management' },
        { name: 'AI Auto Poster', desc: 'AI-powered automatic social media posting' },
        { name: 'Comment Monitor', desc: 'Monitor and respond to social media comments' },
        { name: 'Marketing AI Monitor', desc: 'Track marketing campaign performance' },
        { name: 'AI Marketing Assistant', desc: 'AI-powered marketing content generation' },
        { name: 'Marketing Cost Tracker', desc: 'Track marketing spend and ROI' },
      ]
    },
    {
      id: 'documents',
      title: 'DOCUMENT MANAGEMENT',
      icon: FileText,
      color: 'from-slate-600 to-gray-600',
      description: 'Document storage and collaboration',
      tools: [
        { name: 'Document Storage & Sharing', desc: 'Secure document storage and sharing' },
        { name: 'Document Collaboration', desc: 'Real-time document collaboration' },
        { name: 'Version Control', desc: 'Document version history and rollback' },
        { name: 'Cloud Drive Integration', desc: 'Integration with Google Drive and cloud storage' },
      ]
    },
    {
      id: 'infrastructure',
      title: 'INFRASTRUCTURE & MONITORING',
      icon: Cloud,
      color: 'from-teal-600 to-cyan-600',
      description: 'Infrastructure and network monitoring',
      tools: [
        { name: 'Network Management', desc: 'Network monitoring and management' },
        { name: 'Cloud Monitor', desc: 'AWS, Azure, GCP cloud resource monitoring' },
        { name: 'IoT Management', desc: 'IoT device management and monitoring' },
        { name: 'Web Browser', desc: 'Integrated web browser for secure browsing' },
      ]
    },
    {
      id: 'operations',
      title: 'BUSINESS OPERATIONS',
      icon: BarChart3,
      color: 'from-violet-600 to-purple-600',
      description: 'Business operations and management tools',
      tools: [
        { name: 'Project & Task Management', desc: 'Project planning and task tracking' },
        { name: 'Calendar & Scheduling', desc: 'Shared calendar and meeting scheduling' },
        { name: 'Meetings & Conferencing', desc: 'Video conferencing and meeting management' },
        { name: 'Recovery & Compliance Plan', desc: 'Disaster recovery and business continuity planning' },
        { name: 'Visitor Management', desc: 'Visitor tracking and badge system' },
        { name: 'Company Growth Monitor', desc: 'Business growth metrics and KPIs' },
      ]
    },
    {
      id: 'intelligence',
      title: 'BUSINESS INTELLIGENCE',
      icon: TrendingUp,
      color: 'from-lime-600 to-green-600',
      description: 'Analytics and intelligence gathering',
      tools: [
        { name: 'Competitor Intelligence', desc: 'Monitor competitors and market trends' },
        { name: 'Sentiment Analysis', desc: 'Analyze customer sentiment from social media' },
        { name: 'Analytics', desc: 'Comprehensive analytics and reporting' },
      ]
    },
    {
      id: 'integration',
      title: 'INTEGRATION & AUTOMATION',
      icon: Zap,
      color: 'from-amber-600 to-yellow-600',
      description: 'System integration and automation',
      tools: [
        { name: 'API Integration', desc: 'Custom API integration and webhooks' },
        { name: 'Integration Hub', desc: 'Pre-built integrations marketplace' },
        { name: 'Backend Automation', desc: 'Workflow automation and orchestration' },
        { name: 'AI System Admin', desc: 'AI-powered system administration' },
        { name: 'Developer Station', desc: 'Tools for developers and technical staff' },
        { name: 'Admin Control Panel', desc: 'Administrative controls and settings' },
      ]
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-8 p-8">

        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 p-12 text-white shadow-2xl border border-blue-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10 max-w-3xl">
            <h1 className="text-6xl font-black mb-4">Complete Enterprise Platform</h1>
            <p className="text-2xl text-blue-100 mb-6">Cybertection AI HelpDesk: All-in-One Business Management Solution</p>
            <p className="text-blue-200 text-lg">
              Explore 80+ integrated tools covering Help Desk, CRM, HR, Finance, Security, Marketing, and more. Built for enterprises that need comprehensive automation and intelligent operations.
            </p>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <p className="text-3xl font-black">80+</p>
                <p className="text-sm text-blue-100">Total Tools</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <p className="text-3xl font-black">12</p>
                <p className="text-sm text-blue-100">Categories</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <p className="text-3xl font-black">AI</p>
                <p className="text-sm text-blue-100">Powered</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <p className="text-3xl font-black">24/7</p>
                <p className="text-sm text-blue-100">Monitoring</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tools by Category */}
        <div className="space-y-4">
          {toolCategories.map((category) => {
            const Icon = category.icon
            const isExpanded = expandedCategory === category.id

            return (
              <div key={category.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 shadow-lg overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                  className="w-full p-6 flex items-center justify-between hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`bg-gradient-to-br ${category.color} p-3 rounded-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <h2 className="text-2xl font-black text-white">{category.title}</h2>
                      <p className="text-slate-400 text-sm">{category.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-lg font-bold text-sm">
                      {category.tools.length} tools
                    </span>
                    <ChevronDown
                      className={`w-6 h-6 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  </div>
                </button>

                {/* Tools List */}
                {isExpanded && (
                  <div className="border-t border-blue-500/20 bg-slate-700/30 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {category.tools.map((tool, idx) => (
                        <div key={idx} className="bg-slate-700/50 rounded-lg border border-slate-600 p-4 hover:border-blue-500/50 transition-all hover:shadow-lg hover:shadow-blue-500/10">
                          <h3 className="text-white font-bold mb-2 flex items-center space-x-2">
                            <span className={`w-2 h-2 rounded-full bg-gradient-to-r ${category.color}`}></span>
                            <span>{tool.name}</span>
                          </h3>
                          <p className="text-slate-400 text-sm">{tool.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Key Features Section */}
        <div className="bg-gradient-to-br from-purple-900/30 via-slate-800 to-slate-900 rounded-xl border border-purple-500/20 p-8 shadow-lg">
          <h2 className="text-3xl font-black text-white mb-6">Why Choose Cybertection?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: 'AI-Powered', desc: 'Every tool includes intelligent automation and AI assistance' },
              { title: 'Fully Integrated', desc: 'All tools connect seamlessly with unified data' },
              { title: '24/7 Monitoring', desc: 'Round-the-clock automated monitoring and alerts' },
              { title: 'Enterprise Security', desc: 'Bank-grade encryption and compliance standards' },
              { title: 'Scalable', desc: 'Grows with your business from 10 to 10,000 employees' },
              { title: 'Multi-Channel', desc: 'Support across email, chat, phone, SMS, and social media' },
            ].map((feature, idx) => (
              <div key={idx} className="bg-slate-700/50 rounded-lg p-4 border border-purple-500/20">
                <h4 className="text-white font-bold mb-2">{feature.title}</h4>
                <p className="text-slate-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl border border-orange-500/20 p-8 text-white shadow-lg">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-black mb-4">Ready to Transform Your Business?</h2>
            <p className="text-lg mb-6">
              Cybertection AI HelpDesk is the all-in-one solution for modern enterprises. Start with any module and expand as needed.
            </p>
            <div className="flex flex-wrap gap-4">
              <button className="bg-white text-red-600 px-6 py-3 rounded-lg font-bold hover:bg-slate-100 transition-all">
                Schedule Demo
              </button>
              <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-bold hover:bg-white/10 transition-all">
                View Pricing
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
